import { useState, useEffect, useRef } from 'react'
import type { ChangeEvent } from 'react'
import { SchoolService } from '../services/schools'
import type { School, SchoolInsert, SchoolUpdate } from '../services/schools'
import {
  AI_RESPONSE_POLICY_DEFAULT,
  normalizeAiResponsePolicy,
  type AiResponsePolicy,
} from '../constants/aiResponsePolicy'
import { useAiResponsePolicyOptions } from '../hooks/useAiResponsePolicyOptions'
import { resolveStoragePublicUrl } from '../utils/storageUrl'

interface SchoolFormData {
  name: string
  contact_email: string
  address: string
  representative: string
  phone: string
}

const SchoolManager = () => {
  const [schools, setSchools] = useState<School[]>([])
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingSchool, setEditingSchool] = useState<School | null>(null)
  const [formData, setFormData] = useState<SchoolFormData>({
    name: '',
    contact_email: '',
    address: '',
    representative: '',
    phone: ''
  })
  const [aiEducationPolicy, setAiEducationPolicy] = useState('')
  const [aiResponsePolicy, setAiResponsePolicy] = useState<AiResponsePolicy>(AI_RESPONSE_POLICY_DEFAULT)
  const { options: aiResponsePolicyOptions } = useAiResponsePolicyOptions()
  const [logoUrl, setLogoUrl] = useState<string | null>(null)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [logoDisplayMode, setLogoDisplayMode] = useState<'both' | 'school_only' | 'system'>('both')
  const [isAdminSchool, setIsAdminSchool] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    loadSchools()
  }, [])

  const loadSchools = async () => {
    try {
      setIsLoading(true)
      const schoolData = await SchoolService.getSchools()
      setSchools(schoolData)
    } catch (error) {
      console.error('Failed to load schools:', error)
      setError('学校の読み込みに失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      contact_email: '',
      address: '',
      representative: '',
      phone: ''
    })
    setAiEducationPolicy('')
    setAiResponsePolicy(AI_RESPONSE_POLICY_DEFAULT)
    setLogoUrl(null)
    setLogoFile(null)
    setLogoPreview(null)
    setLogoDisplayMode('both')
    setIsAdminSchool(false)
    setEditingSchool(null)
    setError('')
    setSuccess('')
  }

  const handleLogoChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setError('画像ファイルを選択してください')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('ファイルサイズは5MB以下にしてください')
      return
    }
    setLogoFile(file)
    setLogoPreview(URL.createObjectURL(file))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setIsLoading(true)

    try {
      let resolvedLogoUrl = logoUrl

      if (editingSchool) {
        // Upload logo first if a new file was selected
        if (logoFile) {
          resolvedLogoUrl = await SchoolService.uploadSchoolLogo(editingSchool.id, logoFile)
        }

        const updateData: SchoolUpdate = {
          name: formData.name,
          contact_email: formData.contact_email || null,
          address: formData.address || null,
          representative: formData.representative || null,
          phone: formData.phone || null,
          ai_education_policy: aiEducationPolicy.trim() || null,
          ai_response_policy: aiResponsePolicy,
          logo_url: resolvedLogoUrl,
          logo_display_mode: logoDisplayMode,
        }

        await SchoolService.updateSchool(editingSchool.id, updateData)
        // 管理者用の学校は1校だけなので、他校を落としてから立てる専用メソッドを通す
        if (isAdminSchool !== editingSchool.is_admin_school) {
          await SchoolService.setAdminSchool(isAdminSchool ? editingSchool.id : null)
        }
        setSuccess('学校情報を更新しました')
      } else {
        // Create school first, then upload logo using the new ID
        const insertData: SchoolInsert = {
          name: formData.name,
          contact_email: formData.contact_email || null,
          address: formData.address || null,
          representative: formData.representative || null,
          phone: formData.phone || null,
          ai_education_policy: aiEducationPolicy.trim() || null,
          ai_response_policy: aiResponsePolicy,
          logo_display_mode: logoDisplayMode,
        }

        const newSchool = await SchoolService.createSchool(insertData)

        if (logoFile) {
          resolvedLogoUrl = await SchoolService.uploadSchoolLogo(newSchool.id, logoFile)
          await SchoolService.updateSchool(newSchool.id, { logo_url: resolvedLogoUrl })
        }

        if (isAdminSchool) {
          await SchoolService.setAdminSchool(newSchool.id)
        }

        setSuccess('学校を作成しました')
      }

      await loadSchools()
      resetForm()
      setShowAddForm(false)
    } catch (error: unknown) {
      console.error('School creation/update error:', error)

      let errorMessage = '学校の作成/更新に失敗しました'

      if (error instanceof Error) {
        errorMessage = error.message
      } else if (typeof error === 'object' && error !== null) {
        const err = error as Record<string, unknown>
        if (err.message && typeof err.message === 'string') {
          errorMessage = err.message
        } else if (err.error_description && typeof err.error_description === 'string') {
          errorMessage = err.error_description
        } else if (err.details && typeof err.details === 'string') {
          errorMessage = err.details
        } else {
          errorMessage = `エラーが発生しました: ${JSON.stringify(error)}`
        }
      } else {
        errorMessage = String(error)
      }

      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleEdit = (school: School) => {
    setEditingSchool(school)
    setFormData({
      name: school.name,
      contact_email: school.contact_email || '',
      address: school.address || '',
      representative: school.representative || '',
      phone: school.phone || ''
    })
    setAiEducationPolicy(school.ai_education_policy || '')
    setAiResponsePolicy(normalizeAiResponsePolicy(school.ai_response_policy))
    setLogoUrl(school.logo_url || null)
    setLogoDisplayMode(school.logo_display_mode || 'both')
    setIsAdminSchool(school.is_admin_school === true)
    setLogoFile(null)
    setLogoPreview(null)
    setError('')
    setSuccess('')
    setShowAddForm(true)
  }

  const handleDelete = async (schoolId: string) => {
    if (!confirm('この学校を削除しますか？関連する生徒・先生データも削除される可能性があります。')) return

    try {
      setIsLoading(true)
      await SchoolService.deleteSchool(schoolId)
      setSuccess('学校を削除しました')
      await loadSchools()
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      setError(errorMessage || '学校の削除に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  const inputClass = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">学校管理</h2>
        <button
          onClick={() => {
            resetForm()
            setShowAddForm(true)
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium"
        >
          学校追加
        </button>
      </div>

      {/* Success/Error Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="text-red-800 text-sm">{error}</div>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="text-green-800 text-sm">{success}</div>
        </div>
      )}

      {/* School List */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-medium text-gray-900">
            学校一覧 ({schools.length}校)
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  学校名
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  学校ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  住所
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  代表者
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  電話番号
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  メール
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  作成日
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {schools.map((school) => (
                <tr key={school.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {school.logo_url && (
                        <img src={resolveStoragePublicUrl(school.logo_url)} alt="" className="w-6 h-6 object-contain" />
                      )}
                      <div className="text-sm font-medium text-gray-900">{school.name}</div>
                      {school.is_admin_school && (
                        <span className="text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5 whitespace-nowrap">
                          管理者用
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-xs font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded">
                      {school.id}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {school.address || '未設定'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {school.representative || '未設定'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {school.phone || '未設定'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {school.contact_email || '未設定'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {new Date(school.created_at).toLocaleDateString('ja-JP')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleEdit(school)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      編集
                    </button>
                    <button
                      onClick={() => handleDelete(school.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      削除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit School Form */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 py-8 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {editingSchool ? '学校情報編集' : '新規学校追加'}
            </h3>

            {/* Error/Success Messages in Form */}
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="text-red-800 text-sm">{error}</div>
              </div>
            )}
            {success && (
              <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="text-green-800 text-sm">{success}</div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className={labelClass}>学校名 <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className={inputClass}
                    required
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>住所（任意）</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    className={inputClass}
                    placeholder="〒000-0000 都道府県市区町村..."
                  />
                </div>
                <div>
                  <label className={labelClass}>代表者（任意）</label>
                  <input
                    type="text"
                    value={formData.representative}
                    onChange={(e) => setFormData({...formData, representative: e.target.value})}
                    className={inputClass}
                    placeholder="校長名など"
                  />
                </div>
                <div>
                  <label className={labelClass}>電話番号（任意）</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className={inputClass}
                    placeholder="03-0000-0000"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>連絡先メール（任意）</label>
                  <input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                    className={inputClass}
                  />
                </div>
              </div>

              <hr className="border-gray-200" />

              {/* Logo */}
              <div>
                <label className={labelClass}>学校のロゴ（任意）</label>
                <div className="flex items-center gap-4 mt-1">
                  {(logoPreview || logoUrl) && (
                    <img
                      src={logoPreview || resolveStoragePublicUrl(logoUrl!)}
                      alt="ロゴプレビュー"
                      className="w-16 h-16 object-contain border border-gray-200 rounded bg-gray-50"
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="px-3 py-2 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
                  >
                    ファイルアップロード
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="hidden"
                  />
                  {logoFile && (
                    <span className="text-sm text-gray-600">{logoFile.name}</span>
                  )}
                </div>
              </div>

              {/* Logo Display Mode */}
              <div>
                <label className={labelClass}>ロゴの表示設定</label>
                <select
                  value={logoDisplayMode}
                  onChange={(e) => setLogoDisplayMode(e.target.value as 'both' | 'school_only' | 'system')}
                  className={inputClass}
                >
                  <option value="system">MiraPASSのみ</option>
                  <option value="both">学校ロゴ×MiraPASS</option>
                  <option value="school_only">学校ロゴのみ</option>
                </select>
              </div>

              <hr className="border-gray-200" />

              {/* Admin school */}
              <div>
                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={isAdminSchool}
                    onChange={(e) => setIsAdminSchool(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span className="text-sm text-gray-700">
                    管理者用の学校にする
                    <span className="block text-xs text-gray-500 mt-0.5">
                      学校切替でこの学校を選んでいる間だけ、先生モードのアントレ部宿題を全校まとめて表示・採点できます。
                      他の学校を選んでいる間は、その学校の生徒だけが表示されます。設定できるのは1校のみで、
                      チェックを入れると現在の学校から自動的に切り替わります。
                    </span>
                  </span>
                </label>
              </div>

              <hr className="border-gray-200" />

              {/* AI Settings */}
              <div>
                <label className={labelClass}>AIへ反映させる教育方針（任意）</label>
                <textarea
                  value={aiEducationPolicy}
                  onChange={(e) => setAiEducationPolicy(e.target.value)}
                  rows={4}
                  placeholder="例：&#10;・主体性を育む&#10;・一人一人の夢の実現&#10;・仲間との絆"
                  className={`${inputClass} resize-y`}
                />
              </div>
              <div>
                <label className={labelClass}>AIの回答方針</label>
                <div className="mt-1 space-y-2">
                  {aiResponsePolicyOptions.map((option) => (
                    <label key={option.value} className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="radio"
                        name="aiResponsePolicy"
                        value={option.value}
                        checked={aiResponsePolicy === option.value}
                        onChange={() => setAiResponsePolicy(option.value)}
                        className="mt-0.5"
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex space-x-3 pt-2">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50"
                >
                  {isLoading ? '処理中...' : (editingSchool ? '更新' : '作成')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false)
                    resetForm()
                  }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg font-medium"
                >
                  キャンセル
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default SchoolManager
